import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Search, 
  Package, 
  Truck, 
  MapPin, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Download,
  FileText
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  order_placed: { label: "Order Placed", icon: Package, color: "bg-indigo-100 text-indigo-700" },
  processing: { label: "Processing", icon: Clock, color: "bg-amber-100 text-amber-700" },
  in_transit: { label: "In Transit", icon: Truck, color: "bg-blue-100 text-blue-700" },
  out_for_delivery: { label: "Out for Delivery", icon: MapPin, color: "bg-emerald-100 text-emerald-700" },
  delivered: { label: "Delivered", icon: CheckCircle, color: "bg-green-500 text-white" },
  cancelled: { label: "Cancelled", icon: AlertCircle, color: "bg-red-100 text-red-700" }
};

const statusOrder = ["order_placed", "processing", "in_transit", "out_for_delivery", "delivered"];

const TrackingPage = () => {
  const [searchParams] = useSearchParams();
  const [trackingId, setTrackingId] = useState(searchParams.get("id") || "");
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    const id = searchParams.get("id");
    if (id) {
      setTrackingId(id);
      handleTrack(null, id);
    }
  }, [searchParams]);

  const handleTrack = async (e, id = null) => {
    if (e) e.preventDefault();
    const searchId = id || trackingId.trim();
    if (!searchId) return;

    setLoading(true);
    setSearched(true);
    
    try {
      const response = await axios.get(`${API}/track/${encodeURIComponent(searchId)}`);
      setShipment(response.data);
    } catch (error) {
      setShipment(null);
      if (error.response?.status === 404) {
        toast.error("Shipment not found. Please check your tracking ID.");
      } else {
        toast.error("An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (type) => {
    if (!shipment) return;
    window.open(`${API}/files/${shipment.tracking_id}/${type}`, '_blank');
  };

  const getStatusIndex = (status) => statusOrder.indexOf(status);

  return (
    <div className="min-h-screen pt-16 bg-slate-50" data-testid="tracking-page">
      {/* Hero Section */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 
              className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4 tracking-tight uppercase"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Track Your Shipment
            </h1>
            <p className="text-slate-400 mb-8">
              Enter your tracking ID to get real-time updates on your shipment
            </p>

            {/* Search Form */}
            <form onSubmit={handleTrack} className="max-w-xl mx-auto">
              <div className="flex flex-col sm:flex-row gap-3 p-2 bg-white/10 backdrop-blur-lg rounded-sm border border-white/20">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Enter tracking ID (e.g., SWL-2024-001)"
                    value={trackingId}
                    onChange={(e) => setTrackingId(e.target.value)}
                    className="pl-12 h-12 bg-white border-0 rounded-sm text-slate-900 placeholder:text-slate-500"
                    data-testid="tracking-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="h-12 px-8 bg-blue-600 hover:bg-blue-700 rounded-sm font-semibold"
                  disabled={loading}
                  data-testid="tracking-submit-button"
                >
                  {loading ? "Searching..." : "Track"}
                </Button>
              </div>
            </form>

            <p className="text-slate-500 text-sm mt-4">
              Demo IDs: SWL-2024-001, SWL-2024-002, SWL-2024-003
            </p>
          </motion.div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          {loading && (
            <div className="flex justify-center py-12">
              <div className="spinner w-10 h-10"></div>
            </div>
          )}

          {!loading && searched && !shipment && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-12"
            >
              <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">Shipment Not Found</h3>
              <p className="text-slate-500">
                Please verify your tracking ID and try again.
              </p>
            </motion.div>
          )}

          {!loading && shipment && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-8"
              data-testid="tracking-results"
            >
              {/* Shipment Header */}
              <div className="bg-white rounded-sm border border-slate-200 shadow-sm p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                  <div>
                    <p className="text-sm text-slate-500 uppercase tracking-wider">Tracking ID</p>
                    <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }} data-testid="tracking-id-display">
                      {shipment.tracking_id}
                    </h2>
                  </div>
                  <Badge className={`${statusConfig[shipment.status]?.color} px-4 py-2 text-sm font-medium`} data-testid="shipment-status-badge">
                    {statusConfig[shipment.status]?.label || shipment.status}
                  </Badge>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="p-4 bg-slate-50 rounded-sm">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Origin</p>
                    <p className="font-medium text-slate-900">{shipment.origin}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-sm">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Destination</p>
                    <p className="font-medium text-slate-900">{shipment.destination}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-sm">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Service Type</p>
                    <p className="font-medium text-slate-900 capitalize">{shipment.service_type}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-sm">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Est. Delivery</p>
                    <p className="font-medium text-slate-900">{shipment.estimated_delivery || "N/A"}</p>
                  </div>
                </div>
              </div>

              {/* Progress Timeline */}
              <div className="bg-white rounded-sm border border-slate-200 shadow-sm p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                  Shipment Progress
                </h3>

                {/* Progress Bar */}
                <div className="mb-8">
                  <div className="flex justify-between mb-2">
                    {statusOrder.map((status, index) => {
                      const currentIndex = getStatusIndex(shipment.status);
                      const isCompleted = index <= currentIndex;
                      const isCurrent = index === currentIndex;
                      const config = statusConfig[status];
                      const Icon = config.icon;
                      
                      return (
                        <div key={status} className="flex flex-col items-center flex-1">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-all ${
                            isCompleted 
                              ? isCurrent 
                                ? 'bg-blue-600 text-white ring-4 ring-blue-100' 
                                : 'bg-green-500 text-white'
                              : 'bg-slate-100 text-slate-400'
                          }`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <span className={`text-xs text-center hidden sm:block ${isCompleted ? 'text-slate-900 font-medium' : 'text-slate-400'}`}>
                            {config.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="absolute h-full bg-blue-600 transition-all duration-500"
                      style={{ width: `${(getStatusIndex(shipment.status) / (statusOrder.length - 1)) * 100}%` }}
                    ></div>
                  </div>
                </div>

                {/* Timeline History */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Activity Log</h4>
                  {shipment.tracking_history?.slice().reverse().map((event, index) => {
                    const config = statusConfig[event.status];
                    const Icon = config?.icon || Package;
                    
                    return (
                      <div key={index} className="flex gap-4">
                        <div className="relative">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            index === 0 ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'
                          }`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          {index < shipment.tracking_history.length - 1 && (
                            <div className="absolute top-8 left-1/2 -translate-x-1/2 w-0.5 h-8 bg-slate-200"></div>
                          )}
                        </div>
                        <div className="flex-1 pb-4">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                            <p className="font-medium text-slate-900">{event.description}</p>
                            <span className="text-xs text-slate-500">
                              {new Date(event.timestamp).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3" /> {event.location}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Documents Section */}
              {(shipment.has_pod || shipment.has_invoice) && (
                <div className="bg-white rounded-sm border border-slate-200 shadow-sm p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                    Documents
                  </h3>
                  <div className="flex flex-wrap gap-4">
                    {shipment.has_pod && (
                      <Button 
                        variant="outline" 
                        className="rounded-sm"
                        onClick={() => handleDownload('pod')}
                        data-testid="download-pod-button"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Download POD
                        <Download className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                    {shipment.has_invoice && (
                      <Button 
                        variant="outline" 
                        className="rounded-sm"
                        onClick={() => handleDownload('invoice')}
                        data-testid="download-invoice-button"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Download Invoice
                        <Download className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default TrackingPage;
